<script setup>
import {computed, onMounted, ref} from 'vue'
import SubscriptionPaymentsList from '../../../components/customer-area/user-profile/SubscriptionPaymentsList.vue'

const purchases = ref([])
const subscriptions = ref([])
const subscribableSubscriptions = ref([])
const isLoading = ref(true)
const hasError = ref(false)

const loadPurchases = async () => {
    isLoading.value = true
    hasError.value = false

    try {
        const res = await axios.get(route('user.purchases'))
        purchases.value = res.data.purchases ?? []
        subscriptions.value = res.data.subscriptions ?? []
        subscribableSubscriptions.value = res.data.subscribable_subscriptions ?? []
    } catch (error) {
        hasError.value = true
        console.error(error)
    } finally {
        isLoading.value = false
    }
}

const hasPurchases = computed(() => purchases.value.length > 0)
const hasSubscriptions = computed(() => subscriptions.value.length > 0)
const hasSubscribableSubscriptions = computed(() => subscribableSubscriptions.value.length > 0)

const cancelSubscription = async (subscription) => {
    const res = await axios.post(route('user.subscription.cancel', subscription.uuid))

    if (res.data.success) {
        await loadPurchases()
    } else {
        alert(res.data.message)
    }
}

const receiptUrl = (purchase) => `${route('invoice-link.purchase.receipt.show', purchase.uuid)}?media=screen`
const subscriptionFeedUrl = (subscription) => route('subscription.feed', subscription.id)

onMounted(() => {
    loadPurchases()
})
</script>

<template>
    <div>
        <div v-if="isLoading">
            <list-basic class="mb-16">
                <list-basic-item>
                    <template #title>
                        <mf-translate domainkey="shared:loading"></mf-translate>
                    </template>
                </list-basic-item>
            </list-basic>
        </div>
        <div v-else>
            <list-basic v-if="hasError" class="mb-16">
                <list-basic-item>
                    <template #title>
                        Unable to load purchases.
                    </template>
                </list-basic-item>
            </list-basic>

            <div v-if="!hasError">
                <div v-if="hasSubscriptions">
                    <h1 class="mb-4"><mf-translate domainkey="user:subscriptions.title"></mf-translate></h1>
                    <list-basic class="mb-16">
                        <list-basic-item
                            v-for="subscription in subscriptions"
                            :key="subscription.uuid"
                        >
                            <div>
                                <div class="flex justify-between space-x-2">
                                    <div>
                                        <span
                                            :title="subscription.uuid"
                                            class="font-bold cursor-default"
                                        >{{ subscription.title }}</span>
                                        <p
                                            v-if="subscription.state === 'cancelled' && subscription.access_ends_at_display"
                                            class="mb-0 text-xs text-gray-500"
                                        >
                                            {{ _mft('invoiceLink:purchase.accessEndsOn', {date: subscription.access_ends_at_display}) }}
                                        </p>
                                    </div>
                                    <div>
                                        <div class="flex items-center space-x-2">
                                            <span
                                                v-if="subscription.state === 'paid'"
                                                class="text-myflowGreen text-xs"
                                            >
                                                <mf-translate domainkey="invoiceLink:purchase.subscription.active"></mf-translate>
                                            </span>
                                            <span
                                                v-else-if="subscription.state === 'cancelled'"
                                                class="text-gray-400 text-xs"
                                            >
                                                <mf-translate domainkey="invoiceLink:purchase.state.cancelled"></mf-translate>
                                            </span>
                                            <span
                                                v-else
                                                class="text-gray-400 text-xs"
                                            >
                                                <mf-translate domainkey="invoiceLink:purchase.state.notPaid"></mf-translate>
                                            </span>
                                            <button-with-confirmation
                                                v-if="subscription.state !== 'cancelled'"
                                                style-type="secondary"
                                                size="sm"
                                                confirm-type="danger"
                                                :confirm-text="_mft('shared:confirm.areYouSure')"
                                                :confirm-button-label="_mft('shared:confirm.positive')"
                                                :abort-button-label="_mft('shared:confirm.negative')"
                                                @confirmed="cancelSubscription(subscription)"
                                                class="whitespace-nowrap"
                                            >
                                                <mf-translate
                                                    domainkey="shared:cancel"
                                                ></mf-translate>
                                            </button-with-confirmation>
                                        </div>
                                    </div>

                                </div>
                            </div>
                            <div>
                                <subscription-payments-list :subscription="subscription" />
                            </div>

                        </list-basic-item>
                    </list-basic>
                </div>

                <h1 class="mb-4"><mf-translate domainkey="user:purchases.title"></mf-translate></h1>
                <list-basic class="mb-16">
                    <list-basic-item
                        v-for="purchase in purchases"
                        :key="purchase.uuid"
                    >
                        <template #title>
                            <div class="flex items-center">
                                <span
                                    :title="purchase.uuid"
                                    class="font-bold cursor-default"
                                >{{ purchase.title }}</span>
                            </div>
                        </template>

                        <div>

                            <div>
                                <ul>
                                    <li
                                        v-for="(p, i) in purchase.payments"
                                        :key="p.id"
                                        class="grid grid-cols-2 gap-2 py-1"
                                        :data-payment-id="p.id"
                                    >
                                        <div class="text-sm">
                                            <div class="flex items-center space-x-2">
                                                <a
                                                    :href="route('invoice-link.purchase.payment.receipt.show', [purchase.uuid, p.id])+'?media=screen'"
                                                    target="_blank"
                                                >
                                                    <span class="mf-link-immune mf-link">{{ p.issued_at_user_tz }}</span>
                                                    <i class="fa-regular fa-arrow-up-right-from-square text-xxs ml-1 text-gray-500"></i>
                                                </a>
                                                <span
                                                    v-if="purchase.payment_method === 'invoice'"
                                                    class="text-xs"
                                                >
                                                    {{ _mft('invoiceLink:purchase.state.'+p.state) || p.state }}
                                                </span>
                                                <span
                                                    v-else
                                                    class="bg-white border rounded-md px-1 py-px text-gray-500 text-xs"
                                                >
                                                    {{ _mft('invoiceLink:purchase.state.'+p.state) || p.state }}
                                                </span>
                                            </div>
                                        </div>
                                    </li>

                                </ul>

                            </div>


                        </div>

                    </list-basic-item>
                    <list-basic-item v-if="!hasPurchases">
                        <template #title>
                            <mf-translate domainkey="invoiceLink:purchase.noPurchasesToShow"></mf-translate>
                        </template>
                    </list-basic-item>
                </list-basic>

                <card-basic v-if="hasSubscribableSubscriptions">
                    <template #header>
                        <h2><mf-translate domainkey="customerArea:default.subscriptionsSubscribedTo"></mf-translate></h2>
                    </template>
                    <template #body>
                        <ul class="grid-cols-2 gap-4 md:grid">
                            <li
                                v-for="subscription in subscribableSubscriptions"
                                :key="subscription.id"
                            >
                                <a :href="subscriptionFeedUrl(subscription)">
                                    <h3>
                                        {{ subscription.subscribable?.title }}
                                    </h3>
                                    <p>{{ subscription.subscribable?.provider?.name }}</p>
                                </a>
                            </li>
                        </ul>
                    </template>
                </card-basic>
            </div>
        </div>
    </div>
</template>
